import get from 'lodash.get';
import * as validationHelper from '../helpers/validationHelper';
import { AdminContextTypeApi } from '../modules/session';

export const roles = {
  ORGANIZATION_ADMIN: 4,
  CAMPAIGN_ADMIN: 32,
  EVENT_ADMIN: 64,
  EVENT_ORGANIZER_ADMIN: 128,
  CAMPAIGN_ORGANIZER_ADMIN: 256,
};

export const roleToContextType = {
  [roles.ORGANIZATION_ADMIN]: 'Organization',
  [roles.CAMPAIGN_ADMIN]: 'Campaign',
  [roles.EVENT_ADMIN]: 'Event',
  [roles.EVENT_ORGANIZER_ADMIN]: 'EventOrganizer',
  [roles.CAMPAIGN_ORGANIZER_ADMIN]: 'CampaignOrganizer'
};

// Admin role level options (Owner/Admin/Viewer) for promotion dropdown
export const adminRoleLevelOptions = [
  { key: 'admin', value: 'Admin', text: 'Admin' },
  { key: 'viewer', value: 'Viewer', text: 'Viewer' }
];

// Non-system contexts only support Admin role level
export const nonSystemRoleLevelOptions = [
  { key: 'admin', value: 'Admin', text: 'Admin' }
];

/**
 * Gets available role level options based on context type.
 * System context supports Admin/Viewer, other contexts only support Admin.
 * @param {string} contextType - The selected context type
 * @returns {Array} - Array of role level options
 */
export const getRoleLevelOptions = (contextType) => {
  if (contextType === AdminContextType.System) {
    return adminRoleLevelOptions;
  }
  return nonSystemRoleLevelOptions;
};

// Context type constants
export const AdminContextType = {
  System: 'System',
  Organization: 'Organization',
  Campaign: 'Campaign',
  Event: 'Event',
  EventOrganizer: 'EventOrganizer',
  CampaignOrganizer: 'CampaignOrganizer'
};

// Context type options for promotion dropdown (non-system)
export const contextTypeOptions = [
  { key: 'organization', value: AdminContextType.Organization, text: 'Organization' },
  { key: 'campaign', value: AdminContextType.Campaign, text: 'Campaign' },
  { key: 'event', value: AdminContextType.Event, text: 'Event' },
  { key: 'eventOrganizer', value: AdminContextType.EventOrganizer, text: 'Event Organiser' },
  { key: 'campaignOrganizer', value: AdminContextType.CampaignOrganizer, text: 'Campaign Organiser' }
];

// System context type option (only available to system owners)
export const systemContextTypeOption = { key: 'system', value: AdminContextType.System, text: 'System' };

/**
 * Gets all context type options, optionally including System.
 * @param {boolean} includeSystem - Whether to include System context type (for system owners)
 * @returns {Array} - Array of context type options
 */
export const getContextTypeOptions = (includeSystem = false) => {
  if (includeSystem) {
    return [systemContextTypeOption, ...contextTypeOptions];
  }
  return contextTypeOptions;
};

/**
 * Determines which context types are available for promotion based on existing memberships.
 * Rules:
 * - System membership: no promotions allowed
 * - Organization membership: no promotions allowed
 * - Campaign/Event/EventOrganizer/CampaignOrganizer: only same context type allowed
 * - No memberships: all context types available (including System if isSystemOwner)
 * 
 * @param {Array} adminMemberships - Array of existing admin memberships
 * @param {boolean} isSystemOwner - Whether the current user is a system owner
 * @returns {Array} - Filtered contextTypeOptions that are available for promotion
 */
export const getAvailableContextTypes = (adminMemberships = [], isSystemOwner = false) => {
  // Get base options (include System if current user is system owner)
  const baseOptions = getContextTypeOptions(isSystemOwner);
  
  if (!adminMemberships || adminMemberships.length === 0) {
    return baseOptions;
  }

  const membershipContextTypes = adminMemberships.map(m => m.contextType);

  // System membership: no promotions allowed
  if (membershipContextTypes.includes(AdminContextTypeApi.System)) {
    return [];
  }

  // Organization membership: no promotions allowed
  if (membershipContextTypes.includes(AdminContextTypeApi.Organization)) {
    return [];
  }

  // For Campaign/Event/EventOrganizer/CampaignOrganizer: only allow same context type
  // Find the first non-system, non-organization context type
  const existingContextType = membershipContextTypes.find(ct => 
    ct && ct !== AdminContextTypeApi.System && ct !== AdminContextTypeApi.Organization
  );

  if (existingContextType) {
    // Only allow the same context type (exclude System from these as well)
    // Compare against opt.key which uses API casing (camelCase)
    return contextTypeOptions.filter(opt => 
      opt.key === existingContextType
    );
  }

  // No relevant memberships, all base options available
  return baseOptions;
};

/**
 * Checks if any promotions are available for the given memberships.
 * @param {Array} adminMemberships - Array of existing admin memberships
 * @param {boolean} isSystemOwner - Whether the current user is a system owner
 * @returns {boolean} - True if promotions are available
 */
export const arePromotionsAvailable = (adminMemberships = [], isSystemOwner = false) => {
  return getAvailableContextTypes(adminMemberships, isSystemOwner).length > 0;
};

// Maps context type to its corresponding ID field in the membership object
// Maps API context type to its corresponding ID field in the membership object
const contextTypeToIdField = {
  [AdminContextTypeApi.System]: null, // System has no entity ID
  [AdminContextTypeApi.Organization]: 'organizationId',
  [AdminContextTypeApi.Campaign]: 'campaignId',
  [AdminContextTypeApi.Event]: 'eventId',
  [AdminContextTypeApi.EventOrganizer]: 'eventOrganizerId',
  [AdminContextTypeApi.CampaignOrganizer]: 'campaignOrganizerId'
};

/**
 * Gets the ID field name for a given context type.
 * @param {string} contextType - The context type
 * @returns {string|null} - The ID field name or null if not applicable
 */
export const getIdFieldForContextType = (contextType) => {
  return contextTypeToIdField[contextType] || null;
};

/**
 * Extracts entity IDs from admin memberships by context type.
 * @param {Array} adminMemberships - Array of admin membership objects
 * @param {string} contextType - The context type to filter by (uses AdminContextTypeApi values, e.g. 'campaign', 'organization')
 * @returns {Array} - Array of entity IDs
 */
export const getEntityIdsFromMemberships = (adminMemberships, contextType) => {
  const idField = getIdFieldForContextType(contextType);
  if (!idField) return [];
  
  return (adminMemberships || [])
    .filter(m => m.contextType === contextType)
    .map(m => m[idField])
    .filter(id => id != null);
};

/**
 * Finds a membership by context type.
 * @param {Array} adminMemberships - Array of admin membership objects
 * @param {string} contextType - The context type to find (uses AdminContextTypeApi values, e.g. 'organization', 'system')
 * @returns {Object|undefined} - The matching membership or undefined
 */
export const findMembershipByContextType = (adminMemberships, contextType) => {
  return (adminMemberships || []).find(m => m.contextType === contextType);
};

export const userRolesStrings = {
  systemAdmin: 'systemAdmin',
  organizationAdmin: 'organizationAdmin',
  eventAdmin: 'eventAdmin',
  campaignAdmin: 'campaignAdmin',
  eventOrganizerAdmin: 'eventOrganizerAdmin',
  campaignOrganizerAdmin: 'campaignOrganizerAdmin',

  // Compound role strings (context + level) for PrivateRoute gating
  systemOwner: 'systemOwner',
  systemReadWrite: 'systemReadWrite',
  organizationOwner: 'organizationOwner',
  organizationReadWrite: 'organizationReadWrite'
};

export const userRoleConstants = {
  systemAdmin: 'systemAdmin',
  systemAdminRestricted: 'systemAdminRestricted',
  organizationAdmin: 'organizationAdmin',
  organizationAdminRestricted: 'organizationAdminRestricted',
  eventAdmin: 'eventAdmin',
  eventAdminRestricted: 'eventAdminRestricted',
  campaignAdmin: 'campaignAdmin',
  campaignAdminRestricted: 'campaignAdminRestricted',
  eventOrganizerAdmin: 'eventOrganizerAdmin',
  eventOrganizerAdminRestricted: 'eventOrganizerAdminRestricted',
  campaignOrganizerAdmin: 'campaignOrganizerAdmin',
  campaignOrganizerAdminRestricted: 'campaignOrganizerAdminRestricted'
}

// isSystemOwner is defaulted so the existing single caller keeps working, and so the System
// option stays invisible to anyone who cannot grant a system membership anyway.
export const getRoleOptions = (isSystemAdmin, isOrganizationAdmin, isSystemOwner = false) => [
  {
    text: 'Org admin',
    value: userRoleConstants.organizationAdmin,
    definition: { 
      role: roles.ORGANIZATION_ADMIN,
      restrictExports: false
    },
    isVisible: isSystemAdmin || isOrganizationAdmin
  },
  {
    text: 'Org admin (restricted)',
    value: userRoleConstants.organizationAdminRestricted,
    definition: { 
      role: roles.ORGANIZATION_ADMIN,
      restrictExports: true
    },
    isVisible: isSystemAdmin || isOrganizationAdmin
  },
  {
    text: 'Campaign admin',
    value: userRoleConstants.campaignAdmin,
    definition: { 
      role: roles.CAMPAIGN_ADMIN,
      restrictExports: false
    },
    isVisible: isSystemAdmin || isOrganizationAdmin
  },
  {
    text: 'Event admin',
    value: userRoleConstants.eventAdmin,
    definition: {
      role: roles.EVENT_ADMIN,
      restrictExports: false
    },
    isVisible: isSystemAdmin,
    systemOnly: true
  },
  {
    text: 'Event organizer admin',
    value: userRoleConstants.eventOrganizerAdmin,
    definition: {
      role: roles.EVENT_ORGANIZER_ADMIN,
      restrictExports: false
    },
    isVisible: isSystemAdmin,
    systemOnly: true
  },
  {
    text: 'Campaign organiser admin',
    value: userRoleConstants.campaignOrganizerAdmin,
    definition: {
      role: roles.CAMPAIGN_ORGANIZER_ADMIN,
      restrictExports: false
    },
    isVisible: isSystemAdmin,
    systemOnly: true
  },
  {
    text: 'System admin',
    value: userRoleConstants.systemAdmin,
    // Carries the context type directly rather than a `roles` mask. The roles object mirrors the
    // obsolete Account.Roles flags, and new code should not extend it; the API has taken
    // onboarding.contextType since the membership model replaced those flags.
    definition: {
      contextType: 'System',
      restrictExports: false
    },
    isVisible: isSystemOwner,
    systemOnly: true
  }
];

export const roleConfigurations = {
  [roles.ORGANIZATION_ADMIN]: {
    isPromoteButtonDisabled: (accountDetail, organizationId) => {
      return !organizationId;
    },
    getValidationErrorMessage: (props) => {
      return null;
    },
    getName: (restrictExports) => {
      return restrictExports ? 
        userRoleConstants.organizationAdminRestricted : 
        userRoleConstants.organizationAdmin;
    }
  },
  [roles.CAMPAIGN_ADMIN]: {
    isPromoteButtonDisabled: (accountDetail, organizationId) => {
      const campaignId = get(accountDetail, 'roleDetail.campaignId') || [];
      return campaignId.length === 0;
    },
    getValidationErrorMessage: (props) => {
      const tags = get(props.accountDetail, 'roleDetail.campaignId') || [];
      const tagName = get(props.linkedCampaign, 'id');

      return validationHelper.isUniqueValue(tags, tagName)
        ? ''
        : 'Campaign already exists';
    },
    getName: (restrictExports) => {
      return restrictExports ? 
        userRoleConstants.campaignAdminRestricted : 
        userRoleConstants.campaignAdmin;
    }
  },
  [roles.EVENT_ADMIN]: {
    isPromoteButtonDisabled: (accountDetail, organizationId) => {
      const eventId = get(accountDetail, 'roleDetail.eventId') || [];
      return eventId.length === 0;
    },
    getValidationErrorMessage: (props) => {
      const tags = get(props.accountDetail, 'roleDetail.eventId') || [];
      const tagName = get(props.linkedEvent, 'id');

      return validationHelper.isUniqueValue(tags, tagName)
        ? ''
        : 'Event already exists';
    },
    getName: (restrictExports) => {
      return restrictExports ? 
        userRoleConstants.eventAdminRestricted : 
        userRoleConstants.eventAdmin;
    }
  },
  [roles.EVENT_ORGANIZER_ADMIN]: {
    isPromoteButtonDisabled: (accountDetail, organizationId) => {
      const eventOrganizerId =
        get(accountDetail, 'roleDetail.eventOrganizerId') || [];
      return eventOrganizerId.length === 0;
    },
    getValidationErrorMessage: (props) => {
      const tags =
        get(props.accountDetail, 'roleDetail.eventOrganizerId') || [];
      const tagName = get(props.linkedEventOrganizer, 'id');

      return validationHelper.isUniqueValue(tags, tagName)
        ? ''
        : 'Event organizer already exists';
    },
    getName: (restrictExports) => {
      return restrictExports ? 
        userRoleConstants.eventOrganizerAdminRestricted : 
        userRoleConstants.eventOrganizerAdmin;
    }
  },
  [roles.CAMPAIGN_ORGANIZER_ADMIN]: {
    isPromoteButtonDisabled: (accountDetail, organizationId) => {
      const campaignOrganizerId =
        get(accountDetail, 'roleDetail.campaignOrganizerId') || [];
      return campaignOrganizerId.length === 0;
    },
    getValidationErrorMessage: (props) => {
      const tags =
        get(props.accountDetail, 'roleDetail.campaignOrganizerId') || [];
      const tagName = get(props.linkedCampaignOrganizer, 'id');

      return validationHelper.isUniqueValue(tags, tagName)
        ? ''
        : 'Campaign organiser already exists';
    },
    getName: (restrictExports) => {
      return restrictExports ? 
        userRoleConstants.campaignOrganizerAdminRestricted : 
        userRoleConstants.campaignOrganizerAdmin;
    }
  }
};
